import React, { useEffect, useState } from "react";
import { Button, message, Input, DatePicker, Table, Select, Space } from "antd";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getUserRole } from "../../utils/auth";

const { Option } = Select;

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function SaleReplace() {
  const [saleReplaces, setSaleReplaces] = useState([]);
  const [stores, setStores] = useState([]); // New: Store list for dropdown
  const [loading, setLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    storeName: null, // Changed to null for consistency with Select
    customerName: "",
    search: "",
    fromDate: null,
    toDate: null,
    financialYear: null, // New: Financial year filter
  });
  const [userRole, setUserRole] = useState("");

  // Fetch stores for the dropdown
  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
      // Set initial storeName based on userData
      const userData = JSON.parse(localStorage.getItem('userData')) || {};
      const userStoreId = userData.storeId;
      const userRoleValue = getUserRole();

      if (userRoleValue.role !== 'admin' && userStoreId) {
        const matchingStore = response.data.find((store) => store._id === userStoreId);
        if (matchingStore) {
          setFilters((prev) => ({ ...prev, storeName: matchingStore.name }));
        }
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to fetch stores.");
    }
  };

  // Generate financial years starting from 2024-2025 to current financial year (April-based)
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

    let currentFinancialYearStart, currentFinancialYearEnd;
    if (currentDate < financialYearStart) {
      // If before April, the current financial year is the previous year
      currentFinancialYearStart = currentDate.getFullYear() - 1;
      currentFinancialYearEnd = currentDate.getFullYear();
    } else {
      // If April or later, the current financial year is this year
      currentFinancialYearStart = currentDate.getFullYear();
      currentFinancialYearEnd = currentDate.getFullYear() + 1;
    }

    const financialYears = [];
    let startYear = 2024; // Start from 2024
    let endYear = startYear + 1;

    // Generate financial years from 2024-2025 up to the current financial year
    while (startYear <= currentFinancialYearStart) {
      financialYears.push(`${startYear}-${endYear}`);
      startYear++;
      endYear++;
    }

    return financialYears; // Return in ascending order (oldest to newest)
  };

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value ? value : null,
    }));
  };

  useEffect(() => {
    setUserRole(getUserRole());
    fetchStores(); // Fetch stores on mount
    getSaleReplaces(paginationInfo.current, paginationInfo.pageSize, filters); // Initial fetch with filters
  }, []);

  // Fetch sale replacements with updated filters
  const getSaleReplaces = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const { storeName, search, fromDate, toDate, customerName, financialYear } = filters;
      const response = await API.get("/sales/replaced/paginate", {
        params: {
          page,
          limit: pageSize,
          storeName: storeName || undefined, // Send storeName instead of storeId
          search,
          customerName,
          fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : null,
          toDate: toDate ? dayjs(toDate).format('YYYY-MM-DD') : null,
          financialYear: financialYear || null, // Pass financial year to API
        },
      });

      const salesData = Array.isArray(response?.data?.sales) ? response.data.sales : [];
      setSaleReplaces(salesData);

      setPaginationInfo({
        current: page,
        pageSize: pageSize,
        total: response?.data?.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Error fetching sales:", err);
      message.error("Failed to load sales.");
    } finally {
      setLoading(false);
    }
  };

  // Update sale replacements when filters or pagination change
  useEffect(() => {
    getSaleReplaces(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [paginationInfo.current, paginationInfo.pageSize, filters]);

  const handleDownload = async (data) => {
    try {
      const result = await API.get(`/sales/generate-invoice/${data?._id}`);
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${data?.invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to generate invoice.");
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) => (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Invoice No.",
      render: (_, sale) => sale?.invoiceId,
      key: "invoiceId",
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleSearchChange('search', e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Inv. Date",
      key: "orderDate",
      render: (_, sale) => dayjs(sale?.orderDate).format('YYYY-MM-DD'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys && selectedKeys.length > 0 ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])] : []}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")] : []);
            }}
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleSearchChange("fromDate", selectedKeys[0]);
                  handleSearchChange("toDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearchChange("fromDate", null);
                handleSearchChange("toDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: "Inv. Amount (₹)",
      key: "totalAmount",
      render: (_, sale) => `₹ ${(new Intl.NumberFormat().format(sale?.totalAmount))}`,
    },
    {
      title: "Paid (₹)",
      key: "totalAmount",
      render: (_, sale) => `₹ ${(new Intl.NumberFormat().format(sale?.paidAmount))}`,
    },
    {
      title: "Balance (₹)",
      key: "totalAmount",
      render: (_, sale) => (
        <span style={{ color: sale?.balanceAmount > 0 ? "red" : "green" }}>
          ₹ {new Intl.NumberFormat().format(sale?.balanceAmount || 0)}
        </span>
      ),
    },
    {
      title: "Pay Mode",
      key: "totalAmount",
      render: (_, sale) => `${sale?.paymentMethod?.toUpperCase() || '--'}`,
    },
    {
      title: "Customer",
      key: "customerName",
      render: (_, sale) => (
        <div>
          <div style={{ fontWeight: "bold", fontSize: "12px" }}>
            {sale?.customerId?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {sale?.customerId?.phone || "---"}
          </div>
        </div>
      ),
      filterDropdown: () => (
        <Input
          placeholder="Search Customer"
          onChange={(e) => handleSearchChange("customerName", e.target.value)}
          value={filters.customerName}
          suffix={<SearchOutlined />}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color;
        if (status === "pending") color = "yellow";
        else if (status === "completed") color = "green";
        else if (status === "cancelled") color = "red";
        return (
          <span style={{ color: color, fontWeight: "bold" }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, purchase) => (
        <div>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(purchase)}
            style={{ marginRight: "8px", color: 'green' }}
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <Space style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 150 }}
          placeholder="Store"
          value={filters.storeName}
          onChange={(value) => handleSearchChange("storeName", value)}
          allowClear
                      disabled={userRole!='admin'}

        >
          {stores.map((store) => (
            <Select.Option key={store._id} value={store.name}>
              {store.name}
            </Select.Option>
          ))}
        </Select>
        <Select
          style={{ width: 150 }}
          placeholder="Select Financial Year"
          value={filters.financialYear}
          onChange={(value) => handleSearchChange("financialYear", value)}
          allowClear
        >
          {generateFinancialYears().map((year) => (
            <Select.Option key={year} value={year}>
              {year}
            </Select.Option>
          ))}
        </Select>
      </Space>
      <CustomTable
        columns={columns}
        dataSource={saleReplaces}
        onPaginationChange={(pagination) =>
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          })
        }
        loading={loading}
        style={{ padding: '0 0px' }}
        total={paginationInfo.total}
      />
    </div>
  );
}